import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useHomepageSectionProductsQuery } from "../../store/services/homeProducts";
import ProductSkeleton from "./ProductSkeleton";
import ProductCard from "./ProductCard";
import { toPersianNumber } from "../../utils/persian";

const descriptions = {
  latest_products: "تازه‌ترین مدل‌های اضافه‌شده به جوتی.",
  special_offer: "انتخاب‌های محدود با قیمت بهتر.",
  featured_products: "انتخاب‌های پیشنهادی جوتی برای استایل روزمره.",
  category_products: "گزیده‌ای از محصولات این دسته‌بندی.",
  popular_products: "محبوب‌ترین انتخاب‌های جوتی بر اساس فروش و بازدید."
};

const kickerLabels = {
  latest_products: "New Arrivals",
  special_offer: "Limited Sale",
  featured_products: "Jooti Picks",
  category_products: "Curated",
  popular_products: "Most Loved"
};

const layoutMap = {
  latest_products: "rail",
  special_offer: "sale",
  featured_products: "grid",
  category_products: "rail",
  popular_products: "rail"
};

const getRemaining = (endDate) => {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  if (Number.isNaN(diff) || diff <= 0) {
    return { hours: "۰۰", minutes: "۰۰", seconds: "۰۰", expired: true };
  }
  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours: toPersianNumber(String(hours).padStart(2, "0")),
    minutes: toPersianNumber(String(minutes).padStart(2, "0")),
    seconds: toPersianNumber(String(seconds).padStart(2, "0")),
    expired: false
  };
};

const SaleTimer = ({ endsAt, onExpire }) => {
  const [remaining, setRemaining] = useState(() => getRemaining(endsAt));
  useEffect(() => {
    setRemaining(getRemaining(endsAt));
    const interval = setInterval(() => {
      const nextRemaining = getRemaining(endsAt);
      setRemaining(nextRemaining);
      if (nextRemaining?.expired) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endsAt, onExpire]);

  if (!remaining || remaining.expired) return null;
  return (
    <div className="special-sale-timer">
      <span>پایان فروش</span>
      <div>
        <strong>{remaining.hours}</strong><i>:</i>
        <strong>{remaining.minutes}</strong><i>:</i>
        <strong>{remaining.seconds}</strong>
      </div>
    </div>
  );
};

const hasAvailableStock = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (variants.length) {
    return variants.some((variant) => variant?.is_active !== false && Number(variant?.stock || 0) > 0);
  }
  return Number(product?.stock || 0) > 0;
};

const HomeProduct = ({ section }) => {
  const settings = section?.settings || {};
  const limit = settings.limit || 8;
  const ids = settings.product_ids || [];
  const layout = layoutMap[section.type] || "grid";
  const isScrollable = layout === "rail" || layout === "sale";
  const railRef = useRef(null);
  const [saleExpired, setSaleExpired] = useState(false);
  const [scrollState, setScrollState] = useState({ isScrollable: false, canGoPrev: false, canGoNext: false });
  const { data, isFetching } = useHomepageSectionProductsQuery({ type: section.type, limit, ids });
  const campaign = data?.campaign || null;
  const sectionTitle = campaign?.title || section.title;
  const sectionDescription = campaign?.subtitle || descriptions[section.type] || "محصولات منتخب فروشگاه جوتی.";
  const viewAllLink =
    section.type === "special_offer" ? "/products?sale=1"
    : section.type === "popular_products" ? "/products/1?sort=popular"
    : "/products";

  const visibleProducts = (data?.products || []).filter(hasAvailableStock);

  const getRailMetrics = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return { max: 0, current: 0, isRtl: true };

    const max = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    const isRtl = window.getComputedStyle(rail).direction === "rtl";
    const current = isRtl ? Math.abs(rail.scrollLeft) : rail.scrollLeft;

    return { max, current, isRtl };
  }, []);

  const updateScrollState = useCallback(() => {
    if (!isScrollable) return;

    const { max, current } = getRailMetrics();
    const hasOverflow = max > 8;

    setScrollState({
      isScrollable: hasOverflow,
      canGoPrev: hasOverflow && current > 8,
      canGoNext: hasOverflow && current < max - 8
    });
  }, [getRailMetrics, isScrollable]);

  const scrollProducts = useCallback((direction) => {
    const rail = railRef.current;
    if (!rail) return;

    const { isRtl } = getRailMetrics();
    const amount = Math.max(Math.floor(rail.clientWidth * 0.78), 280);
    const signedAmount = direction === "next"
      ? (isRtl ? -amount : amount)
      : (isRtl ? amount : -amount);

    rail.scrollBy({ left: signedAmount, behavior: "smooth" });

    window.setTimeout(updateScrollState, 360);
  }, [getRailMetrics, updateScrollState]);

  useEffect(() => {
    updateScrollState();

    const rail = railRef.current;
    if (!rail || !isScrollable) return undefined;

    const handleScroll = () => updateScrollState();
    const handleResize = () => updateScrollState();

    rail.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    const timer = window.setTimeout(updateScrollState, 180);

    return () => {
      rail.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(timer);
    };
  }, [isScrollable, visibleProducts.length, updateScrollState]);

  if (isFetching) return <ProductSkeleton />;
  if (!visibleProducts.length) return null;
  if (section.type === "special_offer" && (!campaign || saleExpired)) return null;

  return (
    <section className={`home-dynamic-section home-product-section home-product-section-${section.type} home-product-layout-${layout}`}>
      <div className="home-products-head">
        <div>
          <span className="section-kicker">{kickerLabels[section.type] || "Jooti Edit"}</span>
          <h2>{sectionTitle}</h2>
          <p>{sectionDescription}</p>
        </div>
        <div className="home-products-head-actions">
          {section.type === "special_offer" && <SaleTimer endsAt={campaign?.ends_at} onExpire={() => setSaleExpired(true)} />}
          <Link to={viewAllLink} className={section.type === "special_offer" ? "home-section-link special-sale-head-link" : "home-section-link"}>مشاهده همه</Link>
        </div>
      </div>

      <div className={isScrollable ? `home-products-scroll-shell ${scrollState.isScrollable ? "is-scrollable" : "is-static"}` : ""}>
        {isScrollable && (
          <button type="button" className="home-rail-floating-btn home-rail-floating-btn-right" onClick={() => scrollProducts("prev")} disabled={!scrollState.canGoPrev} aria-label="محصولات قبلی">
            <span><FiChevronRight size={22} /></span>
          </button>
        )}

        <div ref={isScrollable ? railRef : null} onScroll={isScrollable ? updateScrollState : undefined} className={`home-products-grid home-products-${layout}`}>
          {visibleProducts.map((item, index) => (
            <ProductCard product={item} key={item._id || item.id} index={index} layout={layout} showTag={true} />
          ))}
        </div>

        {isScrollable && (
          <button type="button" className="home-rail-floating-btn home-rail-floating-btn-left" onClick={() => scrollProducts("next")} disabled={!scrollState.canGoNext} aria-label="محصولات بعدی">
            <span><FiChevronLeft size={22} /></span>
          </button>
        )}

        {isScrollable && (
          <div className="home-rail-mobile-hint"><span>برای دیدن مدل‌های بیشتر، کارت‌ها را بکشید</span></div>
        )}
      </div>
    </section>
  );
};

export default HomeProduct;
